import type {
  UserOrganizationResponse,
  UserOrganizationsResponse,
} from "@server/types/responseTypes";
import { BILLING_REQUEST_TIMEOUT_MS } from "@/constants/billing";
import { readMetaAttribution } from "@/lib/marketing-events/meta-attribution";
import { useAuthStore } from "@/store/auth";
import {
  apiGet,
  apiPatch,
  apiPost,
  createApiUrl,
  createOrgApiUrl,
} from "@/utils/apiUtils";

const SIGNUP_IDEMPOTENCY_STORAGE_PREFIX = "sully_api_signup_idempotency_key";
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
type PendingSignupAttempt = { idempotencyKey: string; initialName: string };
const pendingSignupAttempts = new Map<string, PendingSignupAttempt>();

function signupAttemptStorageKey(userId: string): string {
  return `${SIGNUP_IDEMPOTENCY_STORAGE_PREFIX}:${encodeURIComponent(userId)}`;
}

function readPendingSignupAttempt(
  userId: string,
): PendingSignupAttempt | undefined {
  const pendingSignupAttempt = pendingSignupAttempts.get(userId);
  if (pendingSignupAttempt) return pendingSignupAttempt;
  const storageKey = signupAttemptStorageKey(userId);
  try {
    const serialized = localStorage.getItem(storageKey);
    if (!serialized) return undefined;
    const candidate = JSON.parse(serialized) as Record<string, unknown>;
    if (
      typeof candidate.idempotencyKey !== "string" ||
      !UUID_PATTERN.test(candidate.idempotencyKey) ||
      typeof candidate.initialName !== "string" ||
      candidate.initialName.length === 0
    ) {
      localStorage.removeItem(storageKey);
      return undefined;
    }
    const restoredAttempt = {
      idempotencyKey: candidate.idempotencyKey,
      initialName: candidate.initialName,
    };
    pendingSignupAttempts.set(userId, restoredAttempt);
    return restoredAttempt;
  } catch {
    // Storage can be unavailable or malformed; in-memory retries remain safe.
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // No cleanup is possible when browser storage itself is unavailable.
    }
  }
  return undefined;
}

function persistPendingSignupAttempt(
  userId: string,
  attempt: PendingSignupAttempt,
): void {
  pendingSignupAttempts.set(userId, attempt);
  try {
    localStorage.setItem(
      signupAttemptStorageKey(userId),
      JSON.stringify(attempt),
    );
  } catch {
    // The request can still use the in-memory key when persistence is blocked.
  }
}

function clearPendingSignupAttempt(userId: string): void {
  pendingSignupAttempts.delete(userId);
  try {
    localStorage.removeItem(signupAttemptStorageKey(userId));
  } catch {
    // Nothing else is required when browser storage is unavailable.
  }
}

/**
 * Performs an authenticated, bodyless GET for organizations available to the
 * current user. The remote read does not mutate client or server organization state.
 *
 * @returns The server-projected organizations and membership roles.
 * @throws Propagates token, network, timeout, non-success response, and parsing errors.
 */
export const fetchOrganizations = async () => {
  const res = await apiGet<UserOrganizationsResponse>(
    createApiUrl("organizations"),
  );
  return res.data;
};

/**
 * Creates an organization and forwards an explicit self-serve enrollment choice.
 * Prepaid responses are returned only after the server owns Stripe provisioning
 * and ledger publication; a retry resumes a durable provisioning organization.
 *
 * @param name Organization display name forwarded to server validation.
 * @param options Optional creation choices. An undefined enrollment value is
 * omitted from the request, while explicit `true` and `false` are preserved.
 * A caller-supplied signup key is reused verbatim; otherwise failed attempts
 * retain the current attempt key until the server returns successfully.
 * @returns The created organization with the caller's membership projection.
 * @throws Propagates token, network, timeout, validation, non-success response,
 * and parsing errors from the authenticated request.
 */
export const createOrganization = async (
  name: string,
  options?: {
    selfServeBillingEnabled?: boolean;
    idempotencyKey?: string;
    resumeOrganizationId?: string;
  },
) => {
  const isSelfServeSignup = options?.selfServeBillingEnabled === true;
  const signupUserId = isSelfServeSignup
    ? useAuthStore.getState().user?.id
    : undefined;
  if (isSelfServeSignup && !signupUserId) {
    throw new Error("Authenticated user is unavailable for prepaid signup");
  }
  const pendingAttempt = signupUserId
    ? readPendingSignupAttempt(signupUserId)
    : undefined;
  const idempotencyKey = isSelfServeSignup
    ? (options.idempotencyKey ??
      pendingAttempt?.idempotencyKey ??
      crypto.randomUUID())
    : undefined;
  if (signupUserId && idempotencyKey) {
    persistPendingSignupAttempt(signupUserId, {
      idempotencyKey,
      initialName: pendingAttempt?.initialName ?? name,
    });
  }

  const metaAttribution = isSelfServeSignup ? readMetaAttribution() : undefined;
  const res = await apiPost<UserOrganizationResponse>(
    createApiUrl("organizations"),
    {
      name,
      ...(metaAttribution ? { metaAttribution } : {}),
      ...(options?.selfServeBillingEnabled !== undefined
        ? { self_serve_billing_enabled: options.selfServeBillingEnabled }
        : {}),
      ...(options?.resumeOrganizationId
        ? { resume_organization_id: options.resumeOrganizationId }
        : {}),
    },
    {
      ...(idempotencyKey
        ? { headers: { "Idempotency-Key": idempotencyKey } }
        : {}),
      timeout: BILLING_REQUEST_TIMEOUT_MS,
    },
  );
  if (signupUserId && idempotencyKey) clearPendingSignupAttempt(signupUserId);
  return res.data;
};

/**
 * Renames the current pending self-serve organization through an authenticated
 * PATCH. The server commits the canonical name and synchronizes provider names;
 * same-name retries are safe, while concurrent canonical changes surface as a
 * conflict for the caller to retry.
 *
 * @param organizationId - Internal organization identifier.
 * @param name - Replacement display name, between 2 and 100 characters.
 * @returns The updated organization returned by the dashboard API.
 * @throws Propagates token, network, timeout, non-success response, and parsing
 *   failures from {@link apiPatch}.
 */
export const updateOrganizationName = async (
  organizationId: string,
  name: string,
) => {
  const res = await apiPatch<UserOrganizationResponse>(
    createOrgApiUrl(organizationId),
    { name },
    { timeout: BILLING_REQUEST_TIMEOUT_MS },
  );
  return res.data;
};
